// @noflow
import Axis from './axis';
import Bars from './bars';
import { last } from 'lodash/fp';
import PropTypes from 'prop-types';
import * as React from 'react';

/************************
 *   Helper functions   *
 ************************/

const _injectBarHeights = (barChartData, offsetHeight, yDomain, yMaxValue) => {
    const yIncr = yMaxValue ? offsetHeight / yMaxValue : 0;
    return barChartData.map(obj => {
        return { ...obj, height: Math.floor(yIncr * obj[yDomain]) };
    });
};

const _injectBarWidths = (barChartData, barWidth) => {
    return barChartData.map(obj => {
        return { ...obj, width: barWidth };
    });
};

const _injectBarX = (barChartData, gutter, marginLeft) => {
    return barChartData.map((obj, i) => {
        return { ...obj, x: marginLeft + obj.width * i + gutter * i };
    });
};

const _injectBarY = (barChartData, offsetHeight, marginTop) => {
    return barChartData.map(obj => {
        return { ...obj, y: Math.floor(offsetHeight - obj.height + marginTop) };
    });
};

const _getBarChartData = ({ compHeight, compWidth, graphData, gutter, margin, yDomain, yTicksInterval }) => {
    const offsetHeight = compHeight - margin.top - margin.bottom;
    const offsetWidth = compWidth - margin.left - margin.right;
    const yHighestValue = Math.max(...graphData.map(obj => obj[yDomain]));
    const yTicksRemainder = yHighestValue % yTicksInterval;
    const yMaxValue = yTicksRemainder > 0 ? yHighestValue - yTicksRemainder + yTicksInterval : yHighestValue;
    const barWidth = offsetWidth / graphData.length - gutter;

    let barChartData = graphData.map(obj => ({ ...obj }));
    barChartData = _injectBarHeights(barChartData, offsetHeight, yDomain, yMaxValue);
    barChartData = _injectBarWidths(barChartData, barWidth);
    barChartData = _injectBarX(barChartData, gutter, margin.left);
    barChartData = _injectBarY(barChartData, offsetHeight, margin.top);

    return barChartData;
};

/*****************
 *   Component   *
 *****************/

export default class Graph extends React.Component {
    static propTypes = {
        animDuration: PropTypes.number.isRequired,
        compHeight: PropTypes.number.isRequired,
        compWidth: PropTypes.number.isRequired,
        graphData: PropTypes.array.isRequired,
        graphStyles: PropTypes.shape({
            axisColour: PropTypes.string.isRequired,
            barColours: PropTypes.array.isRequired,
            barColourMode: PropTypes.string.isRequired,
            fontColour: PropTypes.string.isRequired,
            fontFamily: PropTypes.string.isRequired,
            fontSize: PropTypes.string.isRequired
        }),
        gutter: PropTypes.number.isRequired,
        margin: PropTypes.shape({
            top: PropTypes.number.isRequired,
            left: PropTypes.number.isRequired,
            bottom: PropTypes.number.isRequired,
            right: PropTypes.number.isRequired
        }),
        resetSelectedBarValue: PropTypes.func.isRequired,
        setSelectedBarValue: PropTypes.func.isRequired,
        xDomain: PropTypes.string.isRequired,
        yDomain: PropTypes.string.isRequired,
        yTicksInterval: PropTypes.number.isRequired
    };

    // shouldComponentUpdate doesn't do a deep comparison of the graphData for us, so we have to do a manual check
    shouldComponentUpdate(nextProps) {
        const { graphData, xDomain, compWidth } = this.props;
        const { graphData: nextGraphData, compWidth: nextCompWidth } = nextProps;

        if (nextCompWidth !== compWidth) {
            return true;
        }

        if (nextGraphData.length > 0 && graphData[0] && graphData[0][xDomain]) {
            const currentDataFirstDate = graphData[0][xDomain];
            const currentDataLastDate = last(graphData)[xDomain];
            const nextDataFirstData = nextGraphData[0][xDomain];
            const nextDataLastDate = last(nextGraphData)[xDomain];
            return (
                (currentDataFirstDate !== nextDataFirstData && currentDataLastDate !== nextDataLastDate) ||
                graphData.length !== nextGraphData.length
            );
        }
        return true;
    }

    renderGraph = () => {
        const {
            animDuration,
            compHeight,
            compWidth,
            graphStyles,
            gutter,
            margin,
            resetSelectedBarValue,
            setSelectedBarValue,
            xDomain,
            yDomain,
            yTicksInterval
        } = this.props;

        const barChartData = _getBarChartData(this.props);
        const { axisColour, barColours, barColourMode, fontColour, fontFamily, fontSize } = graphStyles;
        const axisStyles = { axisColour, fontColour, fontFamily, fontSize };

        return (
            <g>
                <Bars
                    animDuration={animDuration}
                    barChartData={barChartData}
                    barColourMode={barColourMode}
                    barColours={barColours}
                    compHeight={compHeight}
                    compWidth={compWidth}
                    gutter={gutter}
                    handleMouseOut={resetSelectedBarValue}
                    handleMouseOver={setSelectedBarValue}
                    marginBottom={margin.bottom}
                    xDomain={xDomain}
                    yDomain={yDomain}
                />
                <Axis
                    axisStyles={axisStyles}
                    barChartData={barChartData}
                    compHeight={compHeight}
                    compWidth={compWidth}
                    margin={margin}
                    xDomain={xDomain}
                    yDomain={yDomain}
                    yTicksInterval={yTicksInterval}
                />
            </g>
        );
    };

    render() {
        const { compHeight, compWidth } = this.props;
        return (
            <svg height={compHeight} name="Graph" width={compWidth}>
                {this.renderGraph()}
            </svg>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/widgets/bar-graph/graph/index.js